import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Anchor,
  ArrowRight,
  BookOpen,
  Calendar,
  Car,
  Check,
  FileText,
  Globe,
  Heart,
  Landmark,
  Lock,
  Mail,
  MapPin,
  Megaphone,
  MessageCircle,
  Network,
  Rocket,
  Scale,
  ShieldCheck,
  Sparkles,
  Star,
  Truck,
  Users,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { anniDallaFondazione } from '../utils/anniUnasca';

const servizi = [
  {
    titolo: 'Formazione Professionale',
    testo: 'Corsi di aggiornamento, seminari e workshop per mantenere alta la qualità della formazione nel settore.',
    immagine: '/images/home/servizi-formazione.webp',
    alt: 'Lezione in aula di un\'autoscuola con istruttore alla lavagna',
    Icona: BookOpen,
  },
  {
    titolo: 'Consulenza Specializzata',
    testo: 'Supporto tecnico e consulenza per questioni normative, amministrative e operative del settore.',
    immagine: '/images/home/servizi-consulenza.webp',
    alt: 'Consulente che esamina una pratica auto alla scrivania',
    Icona: FileText,
  },
  {
    titolo: 'Rete Professionale',
    testo: 'Connessioni e networking con oltre 2000 professionisti del settore in tutta Italia.',
    immagine: '/images/home/servizi-rete.webp',
    alt: 'Professionisti del settore che conversano durante un incontro associativo',
    Icona: Users,
  },
];

const responsabiliSettore = [
  { ruolo: 'Documento Unico', nome: 'Emanuela Pastorello', email: 'segnalazioni_du@unasca.it', Icona: FileText },
  { ruolo: 'Legalità', nome: 'Raoul Rossi', email: 'legalita@unasca.it', Icona: Scale },
  { ruolo: 'Trasporto Merci', nome: 'Santo Petrellese', email: 'merci@unasca.it', Icona: Truck },
  { ruolo: 'Nautica', nome: "Marco D'Agliano", email: 'nautica@unasca.it', Icona: Anchor },
  { ruolo: 'Nazionalizzazioni', nome: 'Gilberto Negretti', email: 'nazionalizzazioni@unasca.it', Icona: Globe },
  { ruolo: 'Rapporti con le Istituzioni', nome: 'Giancarlo Sangalli', email: 'rapporticonleistituzioni@unasca.it', Icona: Landmark },
];

const altriReferenti = [
  { ruolo: 'Unasca Studi Community', nome: 'Alessandro Colalongo', email: 'comunicazione.studi@unasca.it', Icona: MessageCircle },
  { ruolo: 'Rapporti con le Delegazioni Aci', nome: 'Antonio Ago', email: 'delegazioniaci@unasca.it', Icona: Car },
];

const vantaggiRete = [
  {
    titolo: 'Progetto UNASCA',
    testo: "Un'iniziativa esclusiva di UNASCA, la maggiore associazione di categoria a tutela degli studi di consulenza",
    Icona: ShieldCheck,
  },
  {
    titolo: 'Visibilità Nazionale',
    testo: 'Presenza sul portale UNASCA e campagne di comunicazione dedicate',
    Icona: Zap,
  },
  {
    titolo: 'Certificato Ufficiale',
    testo: 'Attestato cartaceo e digitale con elementi di sicurezza anticontraffazione',
    Icona: Lock,
  },
];

const tappeStoria = [
  {
    titolo: 'Fondazione Storica',
    testo: 'Nata nel 1967 per rappresentare gli studi di consulenza automobilistica e le autoscuole italiane',
    Icona: Landmark,
  },
  {
    titolo: 'Innovazione Continua',
    testo: "Sempre all'avanguardia con tecnologie e servizi digitali",
    Icona: Rocket,
  },
  {
    titolo: 'Futuro Promettente',
    testo: 'Continuiamo a crescere e innovare per i nostri associati',
    Icona: Star,
  },
];

const statistiche = [
  { valore: '2000+', etichetta: 'Associati Attivi', Icona: Users },
  { valore: '50+', etichetta: 'Anni di Esperienza', Icona: Calendar },
  { valore: '230', etichetta: 'Volontari', Icona: Heart },
  { valore: '3k', etichetta: 'Associazioni', Icona: Network },
];

const fontCorsivo = { fontFamily: "'Caveat', cursive" };

interface Referente {
  ruolo: string;
  nome: string;
  email: string;
  Icona: LucideIcon;
}

const Occhiello: React.FC<{ children: React.ReactNode; chiaro?: boolean }> = ({ children, chiaro }) => (
  <p
    className={`text-xs font-semibold uppercase tracking-wider mb-3 ${
      chiaro ? 'text-primary-300' : 'text-primary-700 dark:text-primary-400'
    }`}
  >
    {children}
  </p>
);

// Frase scritta a mano con sottolineatura a pennello: decorativa, nascosta ai lettori di schermo
const NotaAMano: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`pointer-events-none select-none text-white ${className}`} aria-hidden="true">
    <span className="block text-3xl lg:text-4xl leading-tight drop-shadow-lg" style={fontCorsivo}>
      {children}
    </span>
    <svg viewBox="0 0 220 16" className="mt-1 w-48 lg:w-56 text-primary-400" fill="none">
      <path d="M3 12 C 60 2, 140 2, 217 8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    </svg>
  </div>
);

const SchedaReferente: React.FC<{ referente: Referente }> = ({ referente: { ruolo, nome, email, Icona } }) => (
  <li className="flex gap-3 py-3">
    <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center">
      <Icona className="w-4 h-4 text-primary-700 dark:text-primary-400" aria-hidden="true" />
    </div>
    <div className="min-w-0">
      <div className="text-xs text-gray-500 dark:text-gray-400">{ruolo}</div>
      <div className="text-sm font-semibold text-gray-900 dark:text-white">{nome}</div>
      <a
        href={`mailto:${email}`}
        className="mt-0.5 inline-flex items-center gap-1.5 text-sm text-primary-700 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline underline-offset-4 break-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
      >
        <Mail className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
        {email}
      </a>
    </div>
  </li>
);

// Il video della testata si carica solo quando non pesa sul primo caricamento
const videoTestataConsentito = () => {
  const connessione = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  return (
    window.matchMedia('(min-width: 1024px)').matches &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
    !connessione?.saveData &&
    !/(^|-)2g$/.test(connessione?.effectiveType || '')
  );
};

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const fotoTestataRef = useRef<HTMLImageElement>(null);
  const [videoPronto, setVideoPronto] = useState(false);

  // Il video parte appena la foto della testata è a schermo: la foto resta il primo contenuto visibile
  useEffect(() => {
    const video = videoRef.current;
    const foto = fotoTestataRef.current;
    if (!video || !foto || !videoTestataConsentito()) return;
    const avvia = () => {
      if (video.getAttribute('src')) return;
      video.src = '/videos/home-studio.mp4';
      video.muted = true;
      video.play().catch(() => undefined);
    };
    if (foto.complete && foto.naturalWidth > 0) avvia();
    else foto.addEventListener('load', avvia, { once: true });
    return () => foto.removeEventListener('load', avvia);
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      <Helmet>
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@600&display=swap" rel="stylesheet" />
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img
            ref={fotoTestataRef}
            src="/images/home/hero-studio.webp"
            srcSet="/images/home/hero-studio-1280.webp 1280w, /images/home/hero-studio.webp 1470w"
            sizes="100vw"
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            className="w-full h-full object-cover object-[70%_center]"
          />
          {/* Video sopra la foto: parte dopo il caricamento e compare in dissolvenza, la foto resta come riserva */}
          <video
            ref={videoRef}
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
            onPlaying={() => setVideoPronto(true)}
            className={`absolute inset-0 w-full h-full object-cover object-[70%_center] transition-opacity duration-500 ${
              videoPronto ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </div>
        <div className="absolute inset-0 bg-primary-950/80 lg:bg-transparent lg:bg-gradient-to-r lg:from-primary-950/95 lg:via-primary-950/70 lg:to-primary-950/15" />
        <div className="absolute inset-y-0 left-0 w-2/3 bg-gradient-to-br from-primary-800/40 to-transparent" aria-hidden="true" />

        <div className="relative container-custom min-h-[34rem] lg:min-h-[38rem] flex items-center py-20 md:py-24">
          <div className="max-w-xl motion-safe:animate-fade-in">
            <Occhiello chiaro>Insieme per il futuro della mobilità</Occhiello>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight leading-tight text-white">
              <span className="block">Benvenuti in</span>
              <span className="block font-unasca text-primary-400 mt-2">UNASCA STUDI</span>
            </h1>

            <p className="text-lg font-semibold text-white mb-4">
              Unione Nazionale Autoscuole e Studi di Consulenza Automobilistica
            </p>

            <p className="text-base md:text-lg text-white/75 mb-10 leading-relaxed">
              L'associazione di riferimento per la formazione professionale,
              consulenza specializzata e supporto nel settore automobilistico.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/diventa-socio" className="btn btn-primary btn-large group focus:ring-offset-primary-950">
                Diventa Associato
                <ArrowRight className="w-5 h-5 ml-2 motion-safe:group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
              {!user && (
                <Link
                  to="/blog"
                  className="btn btn-large group bg-transparent text-white border border-white/40 hover:bg-white/10 focus:ring-white focus:ring-offset-primary-950"
                >
                  Scopri di Più
                  <ArrowRight className="w-5 h-5 ml-2 motion-safe:group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </Link>
              )}
            </div>
          </div>
        </div>

        <NotaAMano className="hidden lg:block absolute right-10 xl:right-16 bottom-10 -rotate-6 text-right">
          Competenze<br />che guidano<br />il cambiamento
        </NotaAMano>
      </section>

      {/* Servizi */}
      <section className="py-16 md:py-20 bg-white dark:bg-black">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-16 items-end mb-10 md:mb-12">
            <div>
              <Occhiello>I Nostri Servizi</Occhiello>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white tracking-tight">
                Valore, competenze, rete
              </h2>
            </div>
            <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              Offriamo una gamma completa di servizi per supportare i professionisti
              del settore automobilistico in tutta Italia.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {servizi.map(({ titolo, testo, immagine, alt, Icona }) => (
              <article
                key={titolo}
                className="relative flex flex-col overflow-hidden rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm"
              >
                <div className="aspect-[16/9] overflow-hidden bg-gray-100 dark:bg-gray-800">
                  <img src={immagine} alt={alt} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="relative px-6 pb-7 pt-10">
                  <div className="absolute -top-7 left-6 w-14 h-14 rounded-full bg-primary-50 dark:bg-primary-900 ring-4 ring-white dark:ring-gray-900 flex items-center justify-center">
                    <Icona className="w-6 h-6 text-primary-700 dark:text-primary-300" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{titolo}</h3>
                  <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 leading-relaxed">{testo}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Prossimi Eventi */}
      <section className="py-16 md:py-20 bg-primary-50/70 dark:bg-gray-950">
        <div className="container-custom">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-4">
              <Occhiello>Prossimi Eventi</Occhiello>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
                Formazione, confronto, crescita
              </h2>
              <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 mb-8">
                Resta aggiornato sui nostri eventi e iniziative
              </p>
              <Link to="/blog" className="btn bg-primary-800 text-white hover:bg-primary-900 focus:ring-primary-500 group">
                Vedi Tutti gli Eventi
                <ArrowRight className="w-5 h-5 ml-2 motion-safe:group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
            </div>

            <Link
              to="/blog/appuntamento-associativo-unasca-roma-21-22-novembre-2026"
              className="lg:col-span-8 group grid sm:grid-cols-5 overflow-hidden rounded-2xl bg-primary-900 shadow-xl hover:shadow-2xl transition-shadow duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              <div className="relative sm:col-span-2 min-h-[15rem] overflow-hidden">
                <img
                  src="/uploads/images/roma-2026-cover.jpg"
                  alt="Il Colosseo illuminato di sera: appuntamento associativo UNASCA a Roma, 21 e 22 novembre 2026"
                  className="absolute inset-0 w-full h-full object-cover object-[35%_center] motion-safe:group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 bg-primary-600 rounded-full shadow">
                  <Sparkles className="w-4 h-4 text-white" aria-hidden="true" />
                  <span className="text-xs font-semibold text-white">Evento in Evidenza</span>
                </div>
              </div>

              <div className="sm:col-span-3 p-7 md:p-8 flex flex-col justify-center">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                  Ci vediamo a Roma! Appuntamento associativo UNASCA
                </h3>

                <p className="text-sm md:text-base text-white/80 mb-6 leading-relaxed">
                  Due giornate al Belstay Roma Aurelia: convegno della Rete Certificata,
                  Centro Studi Cesare Ferrari, Direzione e Consigli Nazionali.
                </p>

                <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  {[
                    { etichetta: 'Data', valore: '21-22 novembre 2026', Icona: Calendar },
                    { etichetta: 'Luogo', valore: 'Roma', Icona: MapPin },
                    { etichetta: 'Community', valore: 'UNASCA Studi', Icona: Users },
                  ].map(({ etichetta, valore, Icona }) => (
                    <div key={etichetta} className="flex items-center gap-3">
                      <div className="w-9 h-9 flex-shrink-0 rounded-full border border-primary-400/40 bg-white/5 flex items-center justify-center">
                        <Icona className="w-4 h-4 text-primary-300" aria-hidden="true" />
                      </div>
                      <div>
                        <dt className="text-xs text-white/60">{etichetta}</dt>
                        <dd className="text-sm font-semibold text-white">{valore}</dd>
                      </div>
                    </div>
                  ))}
                </dl>

                <div className="flex items-center gap-2 text-primary-300 font-semibold text-sm">
                  <span>Scopri tutti i dettagli</span>
                  <ArrowRight className="w-4 h-4 motion-safe:group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Segreteria Nazionale Studi */}
      <section className="py-16 md:py-20 bg-white dark:bg-black">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14">
            <div>
              <Occhiello>Segreteria Nazionale Studi</Occhiello>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight">
                Al fianco dei professionisti
              </h2>
              <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 mb-8">
                Il team che guida e coordina le attività dell'associazione
              </p>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="/images/team2.jpg"
                  alt="Segreteria Nazionale Studi UNASCA"
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="lg:pt-6">
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {[
                  { ruolo: 'Segretario Nazionale Studi', nome: 'Giuseppe Guarino' },
                  { ruolo: 'Vice Segretario Nazionale Studi', nome: 'Francesco Osquino' },
                ].map(({ ruolo, nome }) => (
                  <div
                    key={nome}
                    className="rounded-2xl bg-gradient-to-br from-primary-700 to-primary-900 p-6 text-white shadow-lg"
                  >
                    <div className="text-xs text-white/75 mb-1">{ruolo}</div>
                    <div className="text-lg font-bold">{nome}</div>
                  </div>
                ))}
              </div>

              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Responsabili di Settore
              </h3>
              <ul className="grid sm:grid-cols-2 gap-x-8 sm:divide-x-0">
                {responsabiliSettore.map((r) => (
                  <SchedaReferente key={r.email} referente={r} />
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-10 pt-8 border-t border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              Altri Referenti
            </h3>
            <ul className="grid sm:grid-cols-2 gap-x-14">
              {altriReferenti.map((r) => (
                <SchedaReferente key={r.email} referente={r} />
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Rete Certificata */}
      <section className="relative overflow-hidden bg-primary-50/70 dark:bg-gray-950">
        <ShieldCheck
          className="hidden lg:block absolute -right-10 top-10 w-96 h-96 text-primary-100 dark:text-primary-900/40"
          strokeWidth={1.25}
          aria-hidden="true"
        />

        <div className="relative grid lg:grid-cols-2">
          <div className="relative hidden sm:block min-h-[24rem] lg:min-h-full">
            <img
              src="/images/home/rete-certificata.webp"
              alt="Ingresso di uno studio di consulenza su una strada del centro storico"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <NotaAMano className="absolute left-8 bottom-10 -rotate-6">
              Qualità, trasparenza,<br />valore per i nostri associati
            </NotaAMano>
          </div>

          <div className="px-6 sm:px-8 lg:px-16 py-16 md:py-20">
            <div className="max-w-xl">
              <Occhiello>La nostra garanzia</Occhiello>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight">
                Rete Certificata UNASCA
              </h2>

              <p className="text-lg text-gray-700 dark:text-gray-300 mb-2">
                Un contrassegno di Stato a tutela del cittadino
              </p>

              <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mb-8">
                Certificazione ufficiale rilasciata dal{' '}
                <strong className="text-gray-900 dark:text-white">Poligrafico e Zecca dello Stato</strong>{' '}
                con elementi anticontraffazione
              </p>

              <ul className="space-y-5 mb-10">
                {vantaggiRete.map(({ titolo, testo, Icona }) => (
                  <li key={titolo} className="flex gap-4">
                    <div className="w-10 h-10 flex-shrink-0 bg-primary-700 rounded-full flex items-center justify-center shadow">
                      <Icona className="w-5 h-5 text-white" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-0.5">{titolo}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{testo}</p>
                    </div>
                  </li>
                ))}
              </ul>

              {!user && (
                <div>
                  <Link
                    to="/rete-certificata"
                    className="btn btn-outline group"
                  >
                    Scopri la Rete Certificata
                    <ArrowRight className="w-5 h-5 ml-2 motion-safe:group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                  </Link>
                  <p className="mt-4 text-gray-500 dark:text-gray-400 text-sm">
                    Accedi al tuo account per richiedere l'iscrizione
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {user && (
          <div className="relative container-custom pt-12 md:pt-16 pb-16 md:pb-20">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-8 md:p-10">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0 w-16 h-16 bg-primary-600 rounded-2xl flex items-center justify-center">
                  <Megaphone className="w-8 h-8 text-white" aria-hidden="true" />
                </div>

                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Sei già associato UNASCA?
                  </h3>
                  <p className="text-base text-gray-600 dark:text-gray-400 mb-1">
                    Entra a far parte della <strong className="text-primary-700 dark:text-primary-400">Rete Certificata</strong> e
                    ottieni vantaggi esclusivi per il tuo studio
                  </p>
                  <p className="text-sm text-gray-500">
                    Certificazione ufficiale • Visibilità nazionale • Garanzia UNASCA
                  </p>
                </div>

                <Link
                  to="/rete-certificata"
                  className="flex-shrink-0 inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-7 py-3.5 rounded-full font-semibold shadow-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                >
                  Iscriviti Ora
                  <ArrowRight className="w-5 h-5" aria-hidden="true" />
                </Link>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600 dark:text-gray-400">
                {['Processo 100% online', 'Supporto dedicato', 'Certificazione rapida'].map((voce) => (
                  <div key={voce} className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                    <span>{voce}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Storia */}
      <section className="py-16 md:py-20 bg-white dark:bg-black">
        <div className="container-custom">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-10 items-center">
            <div className="relative hidden lg:block lg:col-span-4 self-stretch rounded-2xl overflow-hidden shadow-lg">
              <img
                src="/images/home/storia-panorama.webp"
                alt="Panorama di un borgo storico italiano al tramonto"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>

            <div className="lg:col-span-4 min-w-0">
              <Occhiello>La Nostra Storia</Occhiello>

              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
                {anniDallaFondazione()} Anni di Eccellenza
              </h2>

              <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                Dal 1967 UNASCA rappresenta il punto di riferimento per gli studi di consulenza automobilistica e le autoscuole italiane.
                Scopri il nostro viaggio attraverso i decenni, i traguardi raggiunti e le sfide superate
                che ci hanno reso ciò che siamo oggi.
              </p>

              <ul className="space-y-4 mb-8">
                {tappeStoria.map(({ titolo, testo, Icona }) => (
                  <li key={titolo} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary-50 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                      <Icona className="w-4 h-4 text-primary-700 dark:text-primary-400" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{titolo}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{testo}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <Link to="/timeline" className="btn btn-primary group">
                <span>Esplora la Timeline</span>
                <ArrowRight className="w-5 h-5 ml-2 motion-safe:group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
            </div>

            <div className="lg:col-span-4 relative min-w-0 rounded-2xl overflow-hidden h-80 sm:h-96 lg:h-auto lg:self-stretch lg:min-h-[22rem] shadow-2xl bg-gray-800">
              <img
                src="/images/home/storia.webp"
                alt="Strada di un centro storico italiano negli anni Sessanta, con le utilitarie dell'epoca"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/40 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="text-6xl font-bold leading-none">{anniDallaFondazione()}</span>
                  <span className="text-base font-semibold">Anni di Storia</span>
                </div>
                <dl className="grid grid-cols-3 gap-3 pt-4 border-t border-white/25">
                  {[
                    { valore: '48k', etichetta: 'Progetti' },
                    { valore: '2000+', etichetta: 'Associati' },
                    { valore: '70m', etichetta: 'Utenti' },
                  ].map(({ valore, etichetta }) => (
                    <div key={etichetta} className="flex flex-col-reverse">
                      <dt className="text-xs text-white/80">{etichetta}</dt>
                      <dd className="text-2xl font-bold">{valore}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-gradient-to-r from-primary-950 via-primary-900 to-primary-800">
        <div
          className="absolute inset-0 opacity-10 text-primary-300"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
            backgroundSize: '24px 24px',
          }}
          aria-hidden="true"
        />

        <div className="relative container-custom py-12 md:py-14 flex flex-col lg:flex-row lg:items-center gap-8">
          <div className="hidden md:flex flex-shrink-0 w-16 h-16 rounded-full bg-white/10 ring-1 ring-white/20 items-center justify-center">
            <Users className="w-8 h-8 text-primary-300" aria-hidden="true" />
          </div>

          <div className="flex-1">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 tracking-tight">
              Pronto a Unirti alla Nostra Comunità?
            </h2>
            <p className="text-base md:text-lg text-white/80 leading-relaxed">
              Diventa parte della più grande associazione di professionisti
              del settore automobilistico in Italia.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
            <Link to="/diventa-socio" className="btn btn-primary group focus:ring-offset-primary-900">
              Associati Ora
              <ArrowRight className="w-5 h-5 ml-2 motion-safe:group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>
            {!user && (
              <Link
                to="/contact"
                className="btn bg-transparent text-white border border-white/40 hover:bg-white/10 focus:ring-white focus:ring-offset-primary-900"
              >
                Contattaci
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Statistiche */}
      <section className="py-10 md:py-12 bg-primary-50/50 dark:bg-gray-950">
        <div className="container-custom">
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {statistiche.map(({ valore, etichetta, Icona }) => (
              <div key={etichetta} className="flex items-center gap-4 justify-center md:justify-start">
                <div className="w-12 h-12 flex-shrink-0 rounded-full bg-white dark:bg-gray-900 ring-1 ring-primary-100 dark:ring-gray-800 flex items-center justify-center">
                  <Icona className="w-5 h-5 text-primary-700 dark:text-primary-400" aria-hidden="true" />
                </div>
                <div className="flex flex-col-reverse">
                  <dt className="text-sm text-gray-600 dark:text-gray-400">{etichetta}</dt>
                  <dd className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">{valore}</dd>
                </div>
              </div>
            ))}
          </dl>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
